import { useTranslation } from "react-i18next";
import {
  Button,
  Dropdown,
  DropdownMenu,
  DropdownItem,
  Flex,
  Input,
} from "@bdo/cornerstone-ui";
import { useNavigate, useSearchParams } from "react-router";
import { Heading } from "@/components";
import { WorkflowCards } from "@/features/workflow-list/WorkflowCards";
import { useDocumentTitle } from "@/hooks";
import { useDispatch, useSelector } from "react-redux";
import { selectWorkflowSearchFilter, setSearchFilter } from "@/features";
import {
  RiArrowUpLine,
  RiArrowDownLine,
  RiCheckLine,
  RiCloseLine,
  RiExpandUpDownLine,
  RiSearchLine,
  RiAddLargeLine,
} from "@remixicon/react";

type SortField = "createdDate" | "updatedDate";
type SortDir = "asc" | "desc";

interface SortState {
  field: SortField;
  direction: SortDir;
}

export function Workflows() {
  const { t } = useTranslation();
  useDocumentTitle({ title: t("pages.workflows.documentTitle") });

  const [searchParams, setSearchParams] = useSearchParams();

  const searchFilter = useSelector(selectWorkflowSearchFilter);

  const sortFieldParam = searchParams.get("sortField") as SortField | null;
  const sortDirParam = searchParams.get("sortDir") as SortDir | null;
  const sortBy: SortState = {
    field: sortFieldParam === "createdDate" ? "createdDate" : "updatedDate",
    direction: sortDirParam === "asc" ? "asc" : "desc",
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();

  function handleSearchChange(value: string) {
    dispatch(setSearchFilter(value));
    // If user uses search field, reset page so we don't risk going out of bounds
    setSearchParams((prev) => {
      prev.delete("page");
      return prev;
    });
  }

  function handleSortByChange(sortOptions: SortState) {
    // Update URL params. Reset page param when sort changes.
    setSearchParams((prev) => {
      if (sortOptions.field === "updatedDate") prev.delete("sortField");
      else prev.set("sortField", sortOptions.field);
      if (sortOptions.direction === "desc") prev.delete("sortDir");
      else prev.set("sortDir", sortOptions.direction);
      prev.delete("page");
      return prev;
    });
  }

  function handleNewWorkflow() {
    navigate("/workflow/create");
  }

  return (
    <Flex
      direction="column"
      as="main"
      className="h-full min-h-0 min-w-0 overflow-hidden"
    >
      <Heading>
        <h1 className="text-lg font-bold text-mono-darker">
          {t("pages.workflows.heading")}
        </h1>
      </Heading>

      <Flex direction="column" className="min-h-0 flex-1 overflow-y-auto">
        <Flex justify="between" align="center" className="py-md px-lg">
          <Flex>
            <Dropdown>
              <Button className="min-w-fit flex gap-sm" variant="transparent">
                <RiExpandUpDownLine className="text-xl" />
                Sort by {sortBy.field === "createdDate"
                  ? "Created"
                  : "Updated"}{" "}
                date {sortBy.direction === "asc" ? "asc" : "desc"}.
              </Button>
              <DropdownMenu>
                <DropdownItem
                  onClick={() =>
                    handleSortByChange({
                      field: "createdDate",
                      direction: "asc",
                    })
                  }
                >
                  <Flex gap="sm" align="center">
                    <RiArrowUpLine />
                    <span>Created date asc.</span>
                    {sortBy.field === "createdDate" &&
                      sortBy.direction === "asc" && (
                        <RiCheckLine className="text-success" />
                      )}
                  </Flex>
                </DropdownItem>
                <DropdownItem
                  onClick={() =>
                    handleSortByChange({
                      field: "createdDate",
                      direction: "desc",
                    })
                  }
                >
                  <Flex gap="sm" align="center">
                    <RiArrowDownLine />
                    <span>Created date desc.</span>
                    {sortBy.field === "createdDate" &&
                      sortBy.direction === "desc" && (
                        <RiCheckLine className="text-success" />
                      )}
                  </Flex>
                </DropdownItem>
                <DropdownItem
                  onClick={() =>
                    handleSortByChange({
                      field: "updatedDate",
                      direction: "asc",
                    })
                  }
                >
                  <Flex gap="sm" align="center">
                    <RiArrowUpLine />
                    <span>Updated date asc.</span>
                    {sortBy.field === "updatedDate" &&
                      sortBy.direction === "asc" && (
                        <RiCheckLine className="text-success" />
                      )}
                  </Flex>
                </DropdownItem>
                <DropdownItem
                  onClick={() =>
                    handleSortByChange({
                      field: "updatedDate",
                      direction: "desc",
                    })
                  }
                >
                  <Flex gap="sm" align="center">
                    <RiArrowDownLine />
                    <span>Updated date desc.</span>
                    {sortBy.field === "updatedDate" &&
                      sortBy.direction === "desc" && (
                        <RiCheckLine className="text-success" />
                      )}
                  </Flex>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </Flex>
          <Flex gap="md">
            <Input
              label={t("pages.workflows.searchInput.label")}
              hideLabel
              placeholder={t("pages.workflows.searchInput.placeholder")}
              prefix={<RiSearchLine />}
              hideSuffixBorder
              suffix={
                searchFilter && (
                  <RiCloseLine
                    onClick={() => handleSearchChange("")}
                    className="cursor-pointer"
                    aria-label="Clear search input"
                  />
                )
              }
              size="sm"
              value={searchFilter}
              onChange={(e) => handleSearchChange(e.target.value)}
              data-testid="workflow-search-input"
            />
            <Button
              variant="primary"
              prefixIcon={<RiAddLargeLine />}
              className="min-w-fit"
              size="sm"
              onClick={handleNewWorkflow}
              data-testid="new-workflow-button"
            >
              {t("newWorkflow.buttonText")}
            </Button>
          </Flex>
        </Flex>

        <WorkflowCards />
      </Flex>
    </Flex>
  );
}
